import { useMemo, useState } from "react";
import useFirestore from "./useFirestore";
import { useFeatureUpdates } from "./useFeatureUpdates";
import { popupCandidate, toDateOrNull } from "../static/updates/featureUpdatePopups";
import type { FeatureUpdate } from "../static/updates/featureUpdates";

/**
 * The popup this user is to be shown now, if any: the newest unseen big update
 * (featureUpdatePopups.ts). Closing it marks THAT update seen (and the older ones), never a newer
 * one the user has not opened.
 */
export function useFeatureUpdatePopup(): { update: FeatureUpdate | null; dismiss: () => void } {
  const { userSettings } = useFirestore();
  const { unseenUpdates, markSeenThrough } = useFeatureUpdates();
  const createdTime = toDateOrNull(userSettings?.createdTime)?.getTime() ?? null;

  const candidate = useMemo(
    () => popupCandidate(unseenUpdates, createdTime == null ? null : new Date(createdTime), new Date()),
    [unseenUpdates, createdTime]
  );
  const [dismissedId, setDismissedId] = useState<number | null>(null);

  const update = candidate && dismissedId !== candidate.id ? candidate : null;
  const dismiss = () => {
    if (!update) return;
    setDismissedId(update.id);
    void markSeenThrough(update.id);
  };
  return { update, dismiss };
}
